// ============================================================================
// useReport — generic report-list + report-summary helpers.
// ============================================================================
// Loop-safety: the `fetcher` arg is captured in a ref so callers don't have to
// memoize the lambda. Effect deps are [enabled, request|filter] only — those
// objects MUST be memoized at the parent (useMemo on primitive inputs).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PaginatedResponse, ApiResponse } from 'api/types';
import type { ReportFilter, ReportListRequest } from 'api/types';

interface ReportListState<T> {
  data: T[];
  pagination: PaginatedResponse<T>['pagination'] | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface ReportSummaryState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useReportList<T>(
  fetcher: (req: ReportListRequest) => Promise<PaginatedResponse<T>>,
  request: ReportListRequest,
  enabled = true,
): ReportListState<T> {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [data, setData]             = useState<T[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<T>['pagination'] | null>(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef.current(request)
      .then((r) => {
        if (cancelled) return;
        setData(r.data ?? []);
        setPagination(r.pagination ?? null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setData([]);
        setPagination(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, request]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    fetcherRef.current(request)
      .then((r) => { setData(r.data ?? []); setPagination(r.pagination ?? null); })
      .catch((e: Error) => { setError(e); setData([]); setPagination(null); })
      .finally(() => setLoading(false));
  }, [enabled, request]);

  return { data, pagination, loading, error, refetch };
}

export function useReportSummary<T>(
  fetcher: (filter: ReportFilter) => Promise<ApiResponse<T>>,
  filter: ReportFilter,
  enabled = true,
): ReportSummaryState<T> {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [data, setData]       = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef.current(filter)
      .then((r) => { if (!cancelled) setData(r?.data ?? null); })
      .catch((e: Error) => { if (!cancelled) { setError(e); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, filter]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    fetcherRef.current(filter)
      .then((r) => setData(r?.data ?? null))
      .catch((e: Error) => { setError(e); setData(null); })
      .finally(() => setLoading(false));
  }, [enabled, filter]);

  return { data, loading, error, refetch };
}
